/**
 * F-148 §4.1–§4.4 (AC5, AC6) CSV import dialog. Parsing is client-side and all-or-nothing via the
 * shared `parseReceivingCsv`; any error means no RPC call. The import itself is prop-driven so the
 * route owns the mutation, the version and the save token. A `duplicate_warning` outcome shows the
 * matching references and requires an acknowledgement before the second call.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Download, FileUp, Loader2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  RECEIVING_CSV_TEMPLATE_FILE_NAME,
  RECEIVING_LIMITS,
  buildReceivingCsvTemplate,
  parseReceivingCsv,
  type ImportReceivingRowsResult,
  type ReceivingCsvFileMeta,
  type ReceivingCsvParseResult,
  type ReceivingCsvRow,
  type ReceivingImportDuplicateMatch,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  issueMessageKey,
  receivingErrorKey,
  receivingErrorOptions,
  withRawValues,
} from '../lib/error-messages';
import { CsvImportErrors } from './csv-import-errors';

export interface CsvImportDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Runs the import RPC; the route supplies draft id, version and token. */
  readonly onImport: (
    rows: ReceivingCsvRow[],
    fileMeta: ReceivingCsvFileMeta,
    acknowledgeDuplicate: boolean,
  ) => Promise<ImportReceivingRowsResult>;
  readonly onImported?: (rowCount: number) => void;
  readonly currentLineCount: number;
  readonly disabled?: boolean;
  /** Injectable for tests; defaults to an anchor click on a Blob URL. */
  readonly downloadTemplate?: (fileName: string, csv: string) => void;
}

type Stage =
  | { kind: 'idle' }
  /** The browser could not read the chosen file (permission, removed media, I/O error). */
  | { kind: 'readError' }
  | { kind: 'parsed'; rows: ReceivingCsvRow[]; fileMeta: ReceivingCsvFileMeta }
  | { kind: 'errors'; result: Extract<ReceivingCsvParseResult, { ok: false }> }
  | {
      kind: 'duplicate';
      rows: ReceivingCsvRow[];
      fileMeta: ReceivingCsvFileMeta;
      matches: ReceivingImportDuplicateMatch[];
    };

function browserDownload(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function CsvImportDialog({
  open,
  onOpenChange,
  onImport,
  onImported,
  currentLineCount,
  disabled = false,
  downloadTemplate = browserDownload,
}: CsvImportDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const id = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [fileName, setFileName] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<unknown>(null);

  useEffect(() => {
    if (!open) {
      setStage({ kind: 'idle' });
      setFileName(null);
      setAcknowledged(false);
      setImporting(false);
      setImportError(null);
    }
  }, [open]);

  const handleFile = async (file: File | null): Promise<void> => {
    setImportError(null);
    setAcknowledged(false);
    if (!file) {
      setStage({ kind: 'idle' });
      setFileName(null);
      return;
    }
    setFileName(file.name);
    if (file.size > RECEIVING_LIMITS.maxCsvBytes) {
      setStage({ kind: 'errors', result: { ok: false, fileError: 'too_large', rowErrors: [] } });
      return;
    }
    let result: ReceivingCsvParseResult;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      result = parseReceivingCsv(bytes, file.name, file.type);
    } catch {
      // Unreadable file: no previous parse result survives, the merchant picks a file again.
      setStage({ kind: 'readError' });
      return;
    }
    if (result.ok) setStage({ kind: 'parsed', rows: result.rows, fileMeta: result.fileMeta });
    else setStage({ kind: 'errors', result });
  };

  const tooManyLines =
    (stage.kind === 'parsed' || stage.kind === 'duplicate') &&
    currentLineCount + stage.rows.length > RECEIVING_LIMITS.maxLines;

  const runImport = async (acknowledge: boolean): Promise<void> => {
    if (stage.kind !== 'parsed' && stage.kind !== 'duplicate') return;
    setImporting(true);
    setImportError(null);
    try {
      const result = await onImport(stage.rows, stage.fileMeta, acknowledge);
      if (result.outcome === 'duplicate_warning') {
        setStage({
          kind: 'duplicate',
          rows: stage.rows,
          fileMeta: stage.fileMeta,
          matches: result.matches,
        });
        setAcknowledged(false);
      } else {
        onImported?.(stage.rows.length);
        onOpenChange(false);
      }
    } catch (err) {
      setImportError(err);
    } finally {
      setImporting(false);
    }
  };

  const canImport =
    !disabled &&
    !importing &&
    !tooManyLines &&
    (stage.kind === 'parsed' || (stage.kind === 'duplicate' && acknowledged));

  return (
    <Dialog open={open} onOpenChange={(next) => (importing ? undefined : onOpenChange(next))}>
      <DialogContent
        dir={dir}
        // Bounded height + inner scroll keep Import/Cancel reachable at 375x667 with a keyboard.
        className="max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto"
        data-testid="csv-import-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('admin.receiving.csv.title')}</DialogTitle>
          <DialogDescription>{t('admin.receiving.csv.templateHelp')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-start">
          <Button
            type="button"
            variant="link"
            className="h-auto min-h-11 px-0"
            onClick={() =>
              downloadTemplate(RECEIVING_CSV_TEMPLATE_FILE_NAME, buildReceivingCsvTemplate())
            }
            data-testid="csv-download-template"
          >
            <Download aria-hidden="true" className="size-4" />
            {t('admin.receiving.draft.downloadTemplate')}
          </Button>

          <div className="space-y-1">
            <Label htmlFor={`${id}-file`} className="text-sm">
              {t('admin.receiving.csv.file')}
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={disabled || importing}
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp aria-hidden="true" className="size-4" />
                {t('admin.receiving.csv.choose')}
              </Button>
              <input
                ref={fileInputRef}
                id={`${id}-file`}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                // The visible "Choose" button is the keyboard control; the label keeps the
                // association for screen readers without a second invisible tab stop.
                tabIndex={-1}
                disabled={disabled || importing}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  // Clear the native value so the same file can be chosen again after an error
                  // (browsers skip `change` when the selection is unchanged).
                  event.target.value = '';
                  void handleFile(file);
                }}
                data-testid="csv-file-input"
              />
              {fileName ? (
                <span
                  className="text-muted-foreground truncate text-sm"
                  data-testid="csv-file-name"
                >
                  <bdi dir="ltr">{fileName}</bdi>
                </span>
              ) : null}
            </div>
          </div>

          {stage.kind === 'readError' ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border p-3 text-sm"
              data-testid="csv-read-error"
            >
              <p className="font-medium">{t('admin.receiving.csv.fileError')}</p>
              <p>{t('admin.receiving.csv.readFailed')}</p>
            </div>
          ) : null}

          {stage.kind === 'errors' ? (
            <CsvImportErrors
              fileError={stage.result.fileError}
              rowErrors={stage.result.rowErrors}
            />
          ) : null}

          {stage.kind === 'parsed' || stage.kind === 'duplicate' ? (
            <p className="text-foreground text-sm" data-testid="csv-parsed-summary">
              {t('admin.receiving.list.lines', { count: stage.rows.length })}
            </p>
          ) : null}

          {tooManyLines ? (
            <p role="alert" className="text-destructive text-sm" data-testid="csv-too-many-lines">
              {t(issueMessageKey('TOO_MANY_LINES'), {
                max: RECEIVING_LIMITS.maxLines,
                count:
                  currentLineCount +
                  (stage.kind === 'parsed' || stage.kind === 'duplicate' ? stage.rows.length : 0),
              })}
            </p>
          ) : null}

          {stage.kind === 'duplicate' ? (
            <div
              role="alert"
              className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100"
              data-testid="csv-duplicate-warning"
            >
              <p>
                {t(
                  'admin.receiving.csv.duplicateWarning',
                  withRawValues({
                    references: stage.matches.map((m) => m.displayReference).join(', '),
                  }),
                )}
              </p>
              <label
                htmlFor={`${id}-ack`}
                className="flex min-h-11 cursor-pointer items-center gap-3"
              >
                <input
                  id={`${id}-ack`}
                  type="checkbox"
                  className="border-input text-admin-lime focus:ring-admin-lime size-5 rounded"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  data-testid="csv-acknowledge"
                />
                <span>{t('admin.receiving.csv.acknowledge')}</span>
              </label>
            </div>
          ) : null}

          {importError !== null && importError !== undefined ? (
            <p role="alert" className="text-destructive text-sm" data-testid="csv-import-error">
              {t(receivingErrorKey(importError), receivingErrorOptions(importError))}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={importing}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            className="min-h-11"
            disabled={!canImport}
            onClick={() => void runImport(stage.kind === 'duplicate')}
            data-testid="csv-import-submit"
          >
            {importing ? (
              <Loader2
                aria-hidden="true"
                className="size-4 animate-spin motion-reduce:animate-none"
              />
            ) : null}
            {importing ? t('admin.receiving.csv.importing') : t('admin.receiving.csv.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
